import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from 'utils/supabase'
import styled from 'styled-components'
import { Button, TextField } from '@mui/material'
import toast from 'react-hot-toast'
import { v4 as uuidv4 } from 'uuid'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ConfirmDialog from 'components/ConfirmDialog'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'

interface ArticleForm {
  date: string
  title: string
  content: string
  linkName: string
  link: string
  image: File | null
  currentImage?: string
}

const defaultValues: ArticleForm = {
  date: '',
  title: '',
  content: '',
  linkName: '',
  link: '',
  image: null,
  currentImage: ''
}

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{'list': 'ordered'}, {'list': 'bullet'}],
    ['link', 'clean']
  ]
}

const formatDateForInput = (dateStr: string): string => {
  try {
    // For existing dates in Polish format (e.g., "20 listopada 2023")
    const date = dayjs(dateStr, 'D MMMM YYYY', 'pl')
    return date.format('YYYY-MM-DD')
  } catch (error) {
    console.error('Date parsing error:', error)
    return dayjs().format('YYYY-MM-DD')
  }
}

export default function EditArticleForm() {
  const [values, setValues] = useState<ArticleForm>(defaultValues)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()
  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false)

  useEffect(() => {
    fetchArticle()
  }, [id])

  const fetchArticle = async () => {
    if (!id) return

    try {
      const { data, error } = await supabase
        .from('fundacja')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      setValues({
        ...data,
        image: null,
        currentImage: data.image
      })
    } catch (error) {
      console.error('Error fetching article:', error)
      toast.error('Błąd podczas pobierania artykułu')
      navigate('/admin/articles')
    }
  }

  const handleValueChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues(prev => ({ ...prev, [name]: e.target.value }))
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setValues(prev => ({ ...prev, image: e.target.files![0] }))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    try {
      let imageUrl = values.currentImage

      if (values.image) {
        const fileExt = values.image.name.split('.').pop()
        const fileName = `${uuidv4()}.${fileExt}`
        
        const { error: uploadError } = await supabase.storage
          .from('articles')
          .upload(fileName, values.image)

        if (uploadError) throw uploadError

        const { data: { publicUrl } } = supabase.storage
          .from('articles')
          .getPublicUrl(fileName)

        imageUrl = publicUrl
      }

      const { error: updateError } = await supabase
        .from('fundacja')
        .update({
          date: values.date,
          title: values.title,
          content: values.content,
          linkName: values.linkName,
          link: values.link,
          image: imageUrl
        })
        .eq('id', id)

      if (updateError) throw updateError

      toast.success('Artykuł został zaktualizowany')
      navigate('/admin/articles')
    } catch (error) {
      toast.error('Błąd podczas aktualizacji artykułu')
      console.error('Error updating article:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleImageDelete = async () => {
    if (!values.currentImage) return

    try {
      const imagePath = values.currentImage.split('/').pop()
      if (imagePath) {
        const { error } = await supabase.storage
          .from('articles')
          .remove([imagePath])

        if (error) throw error
      }

      const { error: updateError } = await supabase
        .from('fundacja')
        .update({ image: null })
        .eq('id', id)

      if (updateError) throw updateError

      setValues(prev => ({ ...prev, currentImage: '' }))
      toast.success('Zdjęcie zostało usunięte')
    } catch (error) {
      toast.error('Błąd podczas usuwania zdjęcia')
      console.error('Error deleting image:', error)
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convert from YYYY-MM-DD to Polish format
    const selectedDate = dayjs(e.target.value)
    const formattedDate = selectedDate.locale('pl').format('D MMMM YYYY')
    setValues(prev => ({ ...prev, date: formattedDate }))
  }

  return (
    <Wrapper>
      <Form onSubmit={handleSubmit}>
        <Header>
          <Title>Edytujesz: {values.title}</Title>
          <ButtonsGroup>
            <Button 
              variant="contained" 
              type="submit"
              disabled={loading}
            >
              {loading ? 'Zapisywanie...' : 'Zapisz zmiany'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/admin/articles')}
            >
              Powrót
            </Button>
          </ButtonsGroup>
        </Header>

        <FormContent>
          <TextField
            required
            fullWidth
            label="Data"
            value={values.date}
            onChange={handleValueChange('date')}
            placeholder="np. 10 stycznia 2024"
            helperText="(np. 20 listopada 2023)"
          />
          
          <TextField
            required
            fullWidth
            label="Tytuł"
            value={values.title}
            onChange={handleValueChange('title')}
          />

          <QuillWrapper>
            <label>Treść *</label>
            <ReactQuill
              value={values.content}
              onChange={(content) => setValues(prev => ({ ...prev, content }))}
              modules={quillModules}
              theme="snow"
              placeholder="Wpisz treść artykułu..."
            />
          </QuillWrapper>

          <TextField
            fullWidth
            label="Nazwa linku"
            value={values.linkName}
            onChange={handleValueChange('linkName')}
            placeholder="np. Przeczytaj całość wywiadu"
          />

          <TextField
            fullWidth
            label="Link"
            value={values.link}
            onChange={handleValueChange('link')}
          />

          {values.currentImage && (
            <CurrentImageWrapper>
              <CurrentImageLabel>Aktualne zdjęcie:</CurrentImageLabel>
              <CurrentImage>
                <img src={values.currentImage} alt="Current article image" />
              </CurrentImage>
              <ImageControlsWrapper>
                <Button 
                  variant="outlined" 
                  component="span"
                  onClick={() => document.getElementById('image-upload')?.click()}
                >
                  Zmień zdjęcie
                </Button>
                <Button 
                  variant="outlined" 
                  color="error" 
                  onClick={() => setDeleteImageDialogOpen(true)}
                >
                  Usuń zdjęcie
                </Button>
              </ImageControlsWrapper>
            </CurrentImageWrapper>
          )}

          {!values.currentImage && (
            <ImageUploadWrapper>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id="image-upload"
                />
                <Button variant="outlined" component="span">
                  Dodaj zdjęcie
                </Button>
              </label>
              {values.image && <ImageName>{values.image.name}</ImageName>}
            </ImageUploadWrapper>
          )}
        </FormContent>
      </Form>

      <ConfirmDialog
        open={deleteImageDialogOpen}
        headerText="Usuwasz zdjęcie"
        message="Czy na pewno chcesz usunąć to zdjęcie? Tej operacji nie można cofnąć."
        onConfirm={handleImageDelete}
        onCancel={() => setDeleteImageDialogOpen(false)}
      />
    </Wrapper>
  )
}

// Styled components
const Wrapper = styled.div`
  background: #f9f9f9;
  min-height: 100vh;
  max-width: 1800px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px;
  margin-bottom: 20px;
  position: sticky;
  top: 0;
  background: #f9f9f9;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 40px;
  max-width: 1200px;
  margin: 0 auto;
`

const QuillWrapper = styled.div`
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    background: white;
  }

  .ql-editor {
    min-height: 200px;
    font-family: inherit;
  }

  label {
    display: block;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 1rem;
  }

  .ql-toolbar {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background: #f8f8f8;
  }

  .ql-container {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`

const CurrentImageWrapper = styled.div`
  margin: 20px 0;
`

const CurrentImageLabel = styled.div`
  margin-bottom: 10px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 1rem;
`

const CurrentImage = styled.div`
  margin-bottom: 15px;
  max-width: 300px;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 4px;
  }
`

const ImageControlsWrapper = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  max-width: 300px;
`

const ImageUploadWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ImageName = styled.span`
  color: #666;
  font-size: 14px;
`

const ButtonsGroup = styled.div`
  display: flex;
  gap: 10px;
` 